
import {defineComponent, getCurrentInstance, onMounted, provide, Ref, ref, watch, watchEffect} from 'vue'
import {variantTypes, attributeTypes} from "@/types/types"
import AttributeValue from "@/components/AttributeValue.vue";
/// <reference types="./../types/EventBus" />

declare global {
    interface Window {
        variantData: variantTypes[];
        attributeData: attributeTypes;
    }
}

export default defineComponent({
    name: 'Attributes',
    components: { AttributeValue },
    setup() {
        const root = getCurrentInstance();

        const variantData: Ref<variantTypes[]> = ref(window.variantData);
        const attributeData: Ref<attributeTypes> = ref(window.attributeData);

        const selectedAttributes: Ref<Map<number, number | null>> = ref(new Map());
        const lastSelectedVariant: Ref<variantTypes | undefined> = ref(undefined);

        const attributes: string[] = Object.keys(attributeData.value);
        attributes.forEach(key => {
            selectedAttributes.value.set(Number.parseInt(key), null);
        });

        provide('selectedAttributes', selectedAttributes);

        const findVariant = (map: Map<number, number | null>) => {
            for (const [key, value] of map) {
                if (value === null)
                    return undefined;
            }

            for (const i in variantData.value) {
                if (!Object.prototype.hasOwnProperty.call(variantData.value, i))
                    continue;

                let variant = variantData.value[i];

                let doesNotFit = false;
                for (const [index, value] of map) {
                    if (Object.prototype.hasOwnProperty.call(variant.variantSelectAttributeValues, index) && variant.variantSelectAttributeValues[index].indexOf(value) <= -1) {
                        doesNotFit = true;
                        break;
                    } else if (!Object.prototype.hasOwnProperty.call(variant.variantSelectAttributeValues, index)) {
                        doesNotFit = true;
                        break;
                    }
                }

                if (doesNotFit)
                    continue;

                return variant;
            }

            return undefined;
        }

        const updatePricePredictions = (variant) => {
            const currentSet: Map<number, number | null> = new Map(selectedAttributes.value);
            for (const i of currentSet.values()) {
                if (i === null)
                    return; // Nejsou zvoleny všechny hodnoty, nelze tvořit predikce
            }

            root && root.emit("attributePricePredictionChanged", {
                predictor: (attribute, value) => {
                    if (currentSet.has(attribute)) {
                        const possibleValues: Map<number, number | null> = new Map(currentSet);
                        possibleValues.set(attribute, value);
                        const possibleVariant = findVariant(possibleValues);
                        if (possibleVariant) {
                            const price = variant !== undefined ? (possibleVariant.plainTextPriceVat - variant.plainTextPriceVat) : possibleVariant.plainTextPriceVat;
                            return {price: price, same: variant !== undefined && variant.id === possibleVariant.id};
                        }

                        return {price: undefined, same: variant === possibleVariant};
                    }
                    return undefined;
                }
            });
        }

        const changeVariant = () => {
            const variant = findVariant(selectedAttributes.value);
            if (variant === undefined || (lastSelectedVariant.value !== undefined && variant.id === lastSelectedVariant.value.id))
                return;

            lastSelectedVariant.value = variant;
            EventBus.getInstance().dispatchEvent('productVariantChanged', variant);
            window.location.hash = '#' + variant.id;

            updatePricePredictions(variant);
        }

        watchEffect(() => { changeVariant() });

        const loadPossibleAttributeVariants = (variant) => {
            if (!lastSelectedVariant.value || lastSelectedVariant.value.id !== variant.id) {
                lastSelectedVariant.value = variant;

                attributes.forEach(key => {
                    const attributeId = Number.parseInt(key);
                    if (Object.prototype.hasOwnProperty.call(variant.variantSelectAttributeValues, attributeId)) {
                        let firstPossible = variant.variantSelectAttributeValues[attributeId].find(e => {
                            return Object.prototype.hasOwnProperty.call(attributeData.value[attributeId].values, Number.parseInt(e));
                        });

                        if (firstPossible !== undefined)
                            firstPossible = Number.parseInt(firstPossible);

                        selectedAttributes.value.set(attributeId, firstPossible);
                    }
                });

                (async() => {
                    while (!Object.prototype.hasOwnProperty.call(window,"EventBusInstance"))
                        await new Promise(resolve => setTimeout(resolve, 400));

                    EventBus.getInstance().dispatchEvent('productVariantChanged', variant);
                })();
            }
        }

        onMounted(() => {
            const hashVariant = variantData.value.find(variant => variant.id === +window.location.hash.substring(1));
            if (window.location.hash && hashVariant) {
                loadPossibleAttributeVariants(hashVariant);
            }
        })

        return {
            attributeData
        }
    }
})

